<script setup>
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import ContactPicker from './ContactPicker.vue';
import PurchaseProjectPicker from './PurchaseProjectPicker.vue';
import {
  TASK_PRIORITIES,
  TASK_TYPES,
  addDays,
  priorityFlagClass,
} from './taskHelpers.js';
import { useTasksStore } from './useTasksStore.js';

const emit = defineEmits(['saved']);

const { t, locale } = useI18n();
const tasksStore = useTasksStore();
const { state } = tasksStore;
const getContactById = useMapGetter('contacts/getContactById');
const isOpen = ref(false);
const isSaving = ref(false);
const editingTaskId = ref(null);
const isContactPickerOpen = ref(false);
const isProjectPickerOpen = ref(false);
const titleRef = ref(null);
const descriptionRef = ref(null);
const pillClearButtonClasses =
  'relative z-[2] shrink-0 inline-flex w-3.5 h-3.5 items-center justify-center p-0 text-n-slate-9 transition-colors hover:text-n-slate-12';

const localDateFromInput = value => {
  const [datePart] = String(value).split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const toInputDateTime = value => {
  if (!value) return '';
  const date = new Date(value);
  const offsetDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60000
  );
  return offsetDate.toISOString().slice(0, 16);
};

const toInputDate = value => toInputDateTime(value).slice(0, 10);

const toInputTime = value => toInputDateTime(value).slice(11, 16);

const form = reactive({
  title: '',
  description: '',
  status: 'todo',
  priority: 'medium',
  type: '',
  dueDate: '',
  dueTime: '',
  dueTimePresent: false,
  assignedToId: state.sellers[0]?.id ?? '',
  linkedContactId: null,
  linkedProjectId: null,
  linkedCloseCircleMemberId: null,
  closeCircleMemberName: '',
  reminderEnabled: false,
  reminderDate: '',
});

const composeDueDate = () => {
  const [hours, minutes] =
    form.dueTimePresent && form.dueTime ? form.dueTime.split(':') : [12, 0];
  const date = localDateFromInput(form.dueDate);
  date.setHours(Number(hours), Number(minutes), 0, 0);
  return date.toISOString();
};

const resetForm = (initial = {}, task = null) => {
  const project = initial.linkedProjectId
    ? tasksStore.findProject(initial.linkedProjectId)
    : null;
  editingTaskId.value = task?.id ?? null;
  form.title = task?.title ?? initial.title ?? '';
  form.description = task?.description ?? initial.description ?? '';
  form.status = task?.status ?? initial.status ?? 'todo';
  form.priority = task?.priority ?? initial.priority ?? 'medium';
  form.type = task?.type ?? initial.type ?? '';
  const dueDate = task?.dueDate ?? initial.dueDate ?? '';
  form.dueDate = dueDate ? toInputDate(dueDate) : '';
  form.dueTimePresent =
    task?.dueTimePresent ?? initial.dueTimePresent ?? Boolean(dueDate);
  form.dueTime = form.dueTimePresent && dueDate ? toInputTime(dueDate) : '';
  form.assignedToId =
    task?.assignedTo?.id ?? initial.assignedToId ?? state.sellers[0]?.id ?? '';
  form.linkedContactId =
    task?.linkedContactId ??
    initial.linkedContactId ??
    project?.contactId ??
    null;
  form.linkedProjectId =
    task?.linkedProjectId ?? initial.linkedProjectId ?? null;
  form.linkedCloseCircleMemberId =
    task?.linkedCloseCircleMemberId ??
    initial.linkedCloseCircleMemberId ??
    null;
  form.closeCircleMemberName =
    task?.closeCircleMemberName ?? initial.closeCircleMemberName ?? '';
  form.reminderEnabled =
    task?.reminder?.enabled ?? initial.reminder?.enabled ?? false;
  form.reminderDate = toInputDateTime(
    task?.reminder?.date ?? initial.reminder?.date ?? ''
  );
};

const autoResize = elRef => {
  const el = elRef.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

const open = async (initial = {}, task = null) => {
  resetForm(initial, task);
  isContactPickerOpen.value = false;
  isProjectPickerOpen.value = false;
  isOpen.value = true;
  await nextTick();
  autoResize(titleRef);
  autoResize(descriptionRef);
};

const close = () => {
  isContactPickerOpen.value = false;
  isProjectPickerOpen.value = false;
  isOpen.value = false;
};

const isValid = computed(
  () => form.title.trim() && form.dueDate && form.assignedToId
);

const selectedContact = computed(() => {
  if (!form.linkedContactId) return null;
  const vuexContact = getContactById.value(form.linkedContactId);
  if (vuexContact) return vuexContact;
  return tasksStore.findContact(form.linkedContactId);
});

const selectedProject = computed(() =>
  form.linkedProjectId ? tasksStore.findProject(form.linkedProjectId) : null
);

const selectedSeller = computed(() =>
  state.sellers.find(seller => seller.id === form.assignedToId)
);

const typeLabel = computed(() =>
  form.type ? t(`TASKS.TYPE.${form.type.toUpperCase()}`) : t('TASKS.FORM.TYPE')
);
const priorityLabel = computed(() =>
  t(`TASKS.PRIORITY.${form.priority.toUpperCase()}`)
);
const priorityFlagClasses = computed(() => priorityFlagClass(form.priority));
const formatPillDate = value => {
  if (!value) return '';
  const date = localDateFromInput(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(locale.value, {
    day: 'numeric',
    month: 'short',
  }).format(date);
};

const dueDateLabel = computed(() =>
  form.dueDate ? formatPillDate(form.dueDate) : t('TASKS.FORM.DUE_DATE')
);

const dueTimeLabel = computed(() =>
  form.dueTimePresent && form.dueTime ? form.dueTime : t('TASKS.FORM.DUE_TIME')
);

const reminderLabel = computed(() => {
  if (form.reminderEnabled && form.reminderDate) {
    return formatPillDate(form.reminderDate);
  }
  return t('TASKS.FORM.REMINDER_SHORT');
});

const contactLabel = computed(
  () => selectedContact.value?.name || t('TASKS.FORM.CONTACT')
);

const projectLabel = computed(
  () => selectedProject.value?.title || t('TASKS.FORM.PROJECT')
);

const enableReminder = () => {
  form.reminderEnabled = true;
  if (!form.reminderDate) {
    form.reminderDate =
      (form.dueDate && toInputDateTime(composeDueDate())) ||
      toInputDateTime(addDays(new Date(), 1));
  }
};

const openContactPicker = () => {
  isContactPickerOpen.value = true;
};

const openNativePicker = event => {
  const input = event.currentTarget;
  input.focus();

  if (typeof input.showPicker !== 'function') return;

  try {
    input.showPicker();
  } catch {
    // Browsers can reject showPicker outside supported contexts; native click still applies.
  }
};

const openReminderPicker = event => {
  enableReminder();
  openNativePicker(event);
};

const onContactSelected = contactId => {
  form.linkedContactId = contactId;
  isContactPickerOpen.value = false;
};

const submit = async () => {
  if (!isValid.value || isSaving.value) return;
  const linkedProject = form.linkedProjectId
    ? tasksStore.findProject(form.linkedProjectId)
    : null;
  const payload = {
    id: editingTaskId.value,
    title: form.title.trim(),
    description: form.description.trim(),
    status: form.status,
    priority: form.priority,
    type: form.type || null,
    dueDate: composeDueDate(),
    dueTimePresent: form.dueTimePresent && Boolean(form.dueTime),
    assignedToId: form.assignedToId,
    assignedTo: state.sellers.find(seller => seller.id === form.assignedToId),
    linkedContactId: form.linkedContactId ?? linkedProject?.contactId ?? null,
    linkedProjectId: form.linkedProjectId,
    linkedCloseCircleMemberId: form.linkedCloseCircleMemberId,
    closeCircleMemberName: form.closeCircleMemberName,
    reminder: {
      enabled: form.reminderEnabled,
      date:
        form.reminderEnabled && form.reminderDate
          ? new Date(form.reminderDate).toISOString()
          : null,
    },
  };
  try {
    isSaving.value = true;
    const savedTask = await tasksStore.saveTask(payload);
    emit('saved', savedTask);
    close();
  } finally {
    isSaving.value = false;
  }
};

const onProjectSelected = project => {
  form.linkedContactId = project.contactId;
  isProjectPickerOpen.value = false;
};

const onDueTimeInput = () => {
  form.dueTimePresent = Boolean(form.dueTime);
};

const clearContact = () => {
  form.linkedContactId = null;
  form.linkedProjectId = null;
};

const clearDueTime = () => {
  form.dueTime = '';
  form.dueTimePresent = false;
};

const clearType = () => {
  form.type = '';
};

const resetPriority = () => {
  form.priority = 'medium';
};

const clearReminder = () => {
  form.reminderDate = '';
  form.reminderEnabled = false;
};

const clearProject = () => {
  form.linkedProjectId = null;
};

watch(
  () => form.linkedContactId,
  contactId => {
    if (!form.linkedProjectId) return;
    const linkedProject = tasksStore.findProject(form.linkedProjectId);
    if (
      linkedProject &&
      String(linkedProject.contactId) !== String(contactId)
    ) {
      form.linkedProjectId = null;
    }
  }
);

defineExpose({ open, close });
</script>

<template>
  <TeleportWithDirection to="body">
    <Transition name="task-form-sheet">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[90] flex items-end justify-center bg-[rgba(17,24,39,0.55)] lg:items-center"
        @click.self="close"
      >
        <form
          class="task-form-panel flex max-h-[94vh] w-full max-w-xl flex-col overflow-hidden rounded-t-[28px] bg-white shadow-2xl lg:rounded-[28px]"
          @submit.prevent="submit"
        >
          <div class="flex flex-1 flex-col overflow-y-auto px-4 pb-6 pt-8">
            <textarea
              ref="titleRef"
              v-model="form.title"
              rows="1"
              required
              class="block min-h-[2.5rem] w-full resize-none appearance-none overflow-hidden border-0 bg-transparent p-0 text-2xl font-semibold leading-10 text-n-slate-12 outline-none ring-0 placeholder:text-n-slate-9 hover:outline-none focus:border-transparent focus:outline-none focus:ring-0 focus-visible:outline-none"
              :placeholder="t('TASKS.FORM.TITLE_PLACEHOLDER')"
              @input="autoResize(titleRef)"
            />

            <textarea
              ref="descriptionRef"
              v-model="form.description"
              rows="2"
              class="block min-h-12 w-full resize-none appearance-none overflow-hidden border-0 bg-transparent p-0 text-base leading-6 text-n-slate-11 outline-none ring-0 placeholder:text-n-slate-9 hover:outline-none focus:border-transparent focus:outline-none focus:ring-0 focus-visible:outline-none"
              :placeholder="t('TASKS.FORM.DESCRIPTION_PLACEHOLDER')"
              @input="autoResize(descriptionRef)"
            />

            <div class="flex flex-wrap gap-2">
              <div
                class="inline-flex h-9 max-w-full cursor-pointer items-center gap-2 px-3 rounded-full bg-n-alpha-1 text-xs font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-2"
                @click="openContactPicker"
              >
                <span class="i-lucide-user size-3.5 shrink-0" />
                <span class="min-w-0 truncate">{{ contactLabel }}</span>
                <button
                  v-if="form.linkedContactId"
                  type="button"
                  :class="pillClearButtonClasses"
                  :aria-label="t('TASKS.FORM.CLEAR_CONTACT')"
                  @click.stop="clearContact"
                >
                  <span class="i-lucide-x size-3.5" />
                </button>
              </div>

              <label
                class="relative inline-flex h-9 items-center gap-2 rounded-full bg-n-alpha-1 px-3 text-xs font-medium text-n-slate-11"
              >
                <span class="i-lucide-calendar size-3.5" />
                <span>{{ dueDateLabel }}</span>
                <input
                  v-model="form.dueDate"
                  type="date"
                  required
                  class="absolute inset-0 cursor-pointer appearance-none opacity-0 outline-none"
                  @click="openNativePicker"
                />
              </label>

              <div
                class="relative inline-flex h-9 max-w-full items-center gap-2 rounded-full bg-n-alpha-1 px-3 text-xs font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-2"
              >
                <span class="i-lucide-clock size-3.5 shrink-0" />
                <span class="truncate">{{ dueTimeLabel }}</span>
                <input
                  v-model="form.dueTime"
                  type="time"
                  class="absolute inset-0 cursor-pointer appearance-none opacity-0 outline-none"
                  @click="openNativePicker"
                  @input="onDueTimeInput"
                />
                <button
                  v-if="form.dueTimePresent"
                  type="button"
                  :class="pillClearButtonClasses"
                  :aria-label="t('TASKS.FORM.CLEAR_DUE_TIME')"
                  @click.stop="clearDueTime"
                >
                  <span class="i-lucide-x size-3.5" />
                </button>
              </div>

              <div
                class="relative inline-flex h-9 max-w-full items-center gap-2 rounded-full bg-n-alpha-1 px-3 text-xs font-medium text-n-slate-11"
              >
                <span class="i-lucide-tag relative z-[1] size-3.5 shrink-0" />
                <span class="relative z-[1] truncate">{{ typeLabel }}</span>
                <select
                  v-model="form.type"
                  class="absolute inset-0 cursor-pointer opacity-0"
                >
                  <option value="">
                    {{ t('TASKS.FORM.TYPE') }}
                  </option>
                  <option v-for="type in TASK_TYPES" :key="type" :value="type">
                    {{ t(`TASKS.TYPE.${type.toUpperCase()}`) }}
                  </option>
                </select>
                <button
                  v-if="form.type"
                  type="button"
                  :class="pillClearButtonClasses"
                  :aria-label="t('TASKS.FORM.CLEAR_TYPE')"
                  @click.stop.prevent="clearType"
                >
                  <span class="i-lucide-x size-3.5" />
                </button>
              </div>

              <div
                class="relative inline-flex h-9 max-w-full items-center gap-2 rounded-full bg-n-alpha-1 px-3 text-xs font-medium text-n-slate-11"
              >
                <span
                  class="i-lucide-flag relative z-[1] size-3.5 shrink-0"
                  :class="priorityFlagClasses"
                />
                <span class="relative z-[1] truncate">{{ priorityLabel }}</span>
                <select
                  v-model="form.priority"
                  class="absolute inset-0 cursor-pointer opacity-0"
                >
                  <option
                    v-for="priority in TASK_PRIORITIES"
                    :key="priority"
                    :value="priority"
                  >
                    {{ t(`TASKS.PRIORITY.${priority.toUpperCase()}`) }}
                  </option>
                </select>
                <button
                  v-if="form.priority !== 'medium'"
                  type="button"
                  :class="pillClearButtonClasses"
                  :aria-label="t('TASKS.FORM.RESET_PRIORITY')"
                  @click.stop.prevent="resetPriority"
                >
                  <span class="i-lucide-x size-3.5" />
                </button>
              </div>

              <div
                class="relative inline-flex h-9 max-w-full items-center gap-2 rounded-full px-3 text-xs font-medium transition-colors"
                :class="
                  form.reminderEnabled
                    ? 'bg-n-ruby-9/10 text-n-ruby-11'
                    : 'bg-n-alpha-1 text-n-slate-11'
                "
                @click="enableReminder"
              >
                <span
                  class="i-lucide-alarm-clock relative z-[1] size-3.5 shrink-0"
                />
                <span class="relative z-[1] truncate">{{ reminderLabel }}</span>
                <input
                  v-model="form.reminderDate"
                  type="datetime-local"
                  class="absolute inset-0 cursor-pointer appearance-none opacity-0 outline-none"
                  @click="openReminderPicker"
                />
                <button
                  v-if="form.reminderEnabled"
                  type="button"
                  :class="pillClearButtonClasses"
                  :aria-label="t('TASKS.FORM.CLEAR_REMINDER')"
                  @click.stop.prevent="clearReminder"
                >
                  <span class="i-lucide-x size-3.5" />
                </button>
              </div>

              <div
                class="inline-flex h-9 max-w-full cursor-pointer items-center gap-2 px-3 rounded-full bg-n-alpha-1 text-xs font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-2"
                @click="isProjectPickerOpen = true"
              >
                <span class="i-lucide-shopping-bag size-3.5 shrink-0" />
                <span class="min-w-0 truncate">{{ projectLabel }}</span>
                <button
                  v-if="form.linkedProjectId"
                  type="button"
                  :class="pillClearButtonClasses"
                  :aria-label="t('TASKS.FORM.CLEAR_PROJECT')"
                  @click.stop="clearProject"
                >
                  <span class="i-lucide-x size-3.5" />
                </button>
              </div>
            </div>
          </div>

          <div
            class="flex items-center justify-between gap-4 border-t border-n-weak bg-white px-4 py-2"
          >
            <label class="flex min-w-0 items-center gap-3">
              <span class="shrink-0 text-sm text-n-slate-10">
                {{ t('TASKS.FORM.ASSIGNEE') }}
              </span>
              <span class="relative min-w-0">
                <span
                  class="flex min-w-0 items-center gap-1 text-base font-medium text-n-slate-12"
                >
                  <span class="truncate">{{ selectedSeller?.name }}</span>
                  <span
                    class="i-lucide-chevron-down size-3.5 shrink-0 text-n-slate-10"
                  />
                </span>
                <select
                  v-model="form.assignedToId"
                  required
                  class="absolute inset-0 w-full cursor-pointer appearance-none bg-transparent opacity-0"
                >
                  <option
                    v-for="seller in state.sellers"
                    :key="seller.id"
                    :value="seller.id"
                  >
                    {{ seller.name }}
                  </option>
                </select>
              </span>
            </label>

            <button
              type="submit"
              class="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-n-brand text-white shadow-sm transition hover:enabled:brightness-110 disabled:bg-n-slate-5 disabled:text-n-slate-9"
              :disabled="!isValid || isSaving"
              :aria-label="t('TASKS.FORM.SAVE')"
            >
              <span
                class="size-4"
                :class="
                  isSaving ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-check'
                "
              />
            </button>
          </div>
        </form>

        <Transition name="task-picker-sheet">
          <div
            v-if="isContactPickerOpen"
            class="fixed inset-0 z-[100] flex items-end justify-center bg-[rgba(17,24,39,0.55)]"
            @click.self="isContactPickerOpen = false"
          >
            <section
              class="task-picker-panel flex h-[82vh] w-full max-w-xl flex-col overflow-hidden rounded-t-[28px] bg-n-background px-4 pb-5 pt-4 shadow-2xl"
            >
              <div class="flex items-center justify-between pb-4">
                <button
                  type="button"
                  class="flex size-10 items-center justify-center rounded-full bg-n-solid-1 text-n-slate-12 shadow-sm"
                  @click="isContactPickerOpen = false"
                >
                  <span class="i-lucide-x size-5" />
                </button>
                <h3 class="truncate px-3 text-sm font-semibold text-n-slate-12">
                  {{ t('TASKS.FORM.SELECT_CONTACT') }}
                </h3>
                <span class="size-10" />
              </div>

              <ContactPicker
                :model-value="form.linkedContactId"
                @update:model-value="onContactSelected"
              />
            </section>
          </div>
        </Transition>

        <Transition name="task-picker-sheet">
          <div
            v-if="isProjectPickerOpen"
            class="fixed inset-0 z-[100] flex items-end justify-center bg-[rgba(17,24,39,0.55)]"
            @click.self="isProjectPickerOpen = false"
          >
            <section
              class="task-picker-panel flex h-[82vh] w-full max-w-xl flex-col overflow-hidden rounded-t-[28px] bg-n-background px-4 pb-5 pt-4 shadow-2xl"
            >
              <div class="flex items-center justify-between pb-4">
                <button
                  type="button"
                  class="flex size-10 items-center justify-center rounded-full bg-n-solid-1 text-n-slate-12 shadow-sm"
                  @click="isProjectPickerOpen = false"
                >
                  <span class="i-lucide-x size-5" />
                </button>
                <h3 class="truncate px-3 text-sm font-semibold text-n-slate-12">
                  {{ t('TASKS.FORM.SELECT_PROJECT') }}
                </h3>
                <span class="size-10" />
              </div>

              <PurchaseProjectPicker
                v-model="form.linkedProjectId"
                :contact-id="form.linkedContactId"
                @select-project="onProjectSelected"
              />
            </section>
          </div>
        </Transition>
      </div>
    </Transition>
  </TeleportWithDirection>
</template>

<style scoped>
.task-form-sheet-enter-active,
.task-form-sheet-leave-active {
  transition: opacity 200ms ease-in-out;
}

.task-form-sheet-enter-from,
.task-form-sheet-leave-to {
  opacity: 0;
}

.task-form-sheet-enter-active .task-form-panel,
.task-form-sheet-leave-active .task-form-panel {
  transition: transform 200ms ease-in-out;
}

.task-form-sheet-enter-from .task-form-panel,
.task-form-sheet-leave-to .task-form-panel {
  transform: translateY(100%);
}

.task-picker-sheet-enter-active,
.task-picker-sheet-leave-active {
  transition: opacity 200ms ease-in-out;
}

.task-picker-sheet-enter-from,
.task-picker-sheet-leave-to {
  opacity: 0;
}

.task-picker-sheet-enter-active .task-picker-panel,
.task-picker-sheet-leave-active .task-picker-panel {
  transition: transform 200ms ease-in-out;
}

.task-picker-sheet-enter-from .task-picker-panel,
.task-picker-sheet-leave-to .task-picker-panel {
  transform: translateY(100%);
}
</style>
